// bGjHYLzm (P2-3) — My Studio persistent product-photo library backed by GCS
// signed URLs (studio-backend POST /signed-url + GET /uploads). Firebase-token
// gated. Project-agnostic: reuses the firebase-auth.js app via getApp(), so it
// works before/after the studio-prod cutover without change.
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { authedFetch, renderStatus, vt } from '@studio/verify-email';

const section   = document.querySelector('[data-uploads-section]');
const dashboard = document.querySelector('[data-studio-dashboard]');

// Guard: needs the section, a backend URL, and firebase-auth.js to have inited.
if (section && dashboard && getApps().length) {
  const BACKEND  = (dashboard.getAttribute('data-backend-url') || '').replace(/\/$/, '');
  const grid     = section.querySelector('[data-uploads-grid]');
  const countEl  = section.querySelector('[data-uploads-count]');
  const input    = section.querySelector('[data-uploads-input]');
  const drop      = section.querySelector('[data-uploads-drop]');
  const addLabel  = section.querySelector('[data-uploads-add-label]');
  const statusEl  = section.querySelector('[data-uploads-status]');
  const auth = getAuth(getApp());
  let cap = 30;
  let busy = false;

  // uGqPRiwp: `status` is the HTTP status behind `msg`. 403 = the backend's
  // email_verified gate — the shared writer mounts the resend control so the
  // message is actionable.
  const setStatus = (msg, isErr, status) => renderStatus(statusEl, msg, isErr, status);
  async function api(path, opts) {
    // Token, Authorization header and the retry-once on the email_verified
    // gate: authedFetch (studio-verify-email.js).
    const res = await authedFetch(BACKEND + path, opts);
    if (!res.ok) {
      let msg = 'Request failed (' + res.status + ')';
      if (res.status === 403) msg = vt('gate_uploads');
      if (res.status === 429) msg = 'You have reached the ' + cap + '-photo limit.';
      const e = new Error(msg); e.status = res.status; throw e;
    }
    return res.status === 204 ? null : res.json();
  }

  function render(list) {
    if (!grid) return;
    grid.textContent = '';
    list.forEach((item) => {
      const fig = document.createElement('figure');
      fig.className = 'ed-studio-uploads-item';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = item.url;              // signed GET URL (safe as src)
      img.alt = item.name || 'Uploaded photo';
      fig.appendChild(img);
      grid.appendChild(fig);
    });
    if (countEl) countEl.textContent = list.length + ' / ' + cap;
    const full = list.length >= cap;
    if (drop) drop.classList.toggle('is-disabled', full || busy);
    if (input) input.disabled = full || busy;
    if (addLabel) addLabel.textContent = full ? 'Photo limit reached' : (busy ? 'Uploading…' : 'Add photos');
  }

  async function refresh() {
    try {
      const data = await api('/uploads', { method: 'GET' });
      if (data && typeof data.cap === 'number') cap = data.cap;
      render((data && data.uploads) || []);
    } catch (e) {
      if (e.message !== 'not-signed-in') setStatus(e.message, true, e.status);
    }
  }

  async function uploadOne(file) {
    // 1) ask the backend for a scoped, size-bounded V4 signed PUT URL
    const signed = await api('/signed-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size }),
    });
    // 2) PUT straight to GCS, echoing the required signed headers (contract).
    // Content-Type must match what the V4 signature was computed over — echo the
    // backend's value if present, else fall back to what we sent.
    const headers = Object.assign({ 'Content-Type': signed.contentType || file.type }, signed.requiredHeaders || {});
    const put = await fetch(signed.url, { method: 'PUT', headers, body: file });
    if (!put.ok) throw new Error('Upload failed (' + put.status + ')');
  }

  const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']); // matches the input accept attr
  async function handleFiles(files) {
    const images = [...files].filter((f) => ALLOWED_TYPES.has(f.type));
    if (!images.length || busy) return;
    busy = true; setStatus('');
    // Non-destructive busy state: keep the library rendered, dim it + lock inputs.
    if (grid) grid.classList.add('is-uploading');
    if (drop) drop.classList.add('is-disabled');
    if (input) input.disabled = true;
    if (addLabel) addLabel.textContent = 'Uploading…';
    const failed = [];
    let failedStatus = 0;
    try {
      for (const file of images) {
        if (file.size > 15 * 1024 * 1024) { failed.push(file.name + ' (over 15 MB)'); continue; }
        try {
          await uploadOne(file);
        } catch (e) {
          failed.push(file.name + ' (' + e.message + ')');
          // Remember the gate status so the batch summary below can still
          // offer the resend control (a 403 fails every file in the batch).
          if (e.status) failedStatus = e.status;
        }
      }
      if (failed.length) setStatus('Failed to upload: ' + failed.join(', '), true, failedStatus);
    } finally {
      busy = false;
      if (grid) grid.classList.remove('is-uploading');
      await refresh();
    }
  }

  if (input) input.addEventListener('change', (e) => { handleFiles(e.target.files); input.value = ''; });
  if (drop) {
    drop.addEventListener('dragover',  (e) => { e.preventDefault(); drop.classList.add('is-over'); });
    drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
    drop.addEventListener('drop', (e) => { e.preventDefault(); drop.classList.remove('is-over'); handleFiles(e.dataTransfer.files); });
  }

  onAuthStateChanged(auth, (user) => {
    if (user) { section.hidden = false; refresh(); }
    else { section.hidden = true; }
  });
}
